import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary component that catches React errors and displays them visually.
 * Useful for debugging blank screens on mobile devices.
 */
export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('🔴 Error Boundary Caught:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Store error in localStorage for persistence across refreshes
    try {
      const errorLog = {
        timestamp: new Date().toISOString(),
        message: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
      };
      localStorage.setItem('chathtl_last_error', JSON.stringify(errorLog));
    } catch (e) {
      console.error('Failed to store error in localStorage:', e);
    }
  }

  private handleReload = () => {
    // Clear error state and reload
    localStorage.removeItem('chathtl_last_error');
    window.location.reload();
  };

  private handleClearCache = async () => {
    try {
      // Clear service worker cache
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // Clear localStorage
      localStorage.clear();

      // Reload
      window.location.reload();
    } catch (e) {
      console.error('Failed to clear cache:', e);
      alert('Could not clear cache. Please try manually in Safari settings.');
    }
  };

  public render() {
    if (this.state.hasError) {
      const { error, errorInfo } = this.state;

      return (
        <div className="min-h-screen bg-red-50 dark:bg-red-900/20 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white dark:bg-neutral-black rounded-lg shadow-lg p-6">
            <div className="flex items-start space-x-3 mb-4">
              <div className="flex-shrink-0">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-red-600 dark:text-red-400 mb-2">
                  Application Error
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  The app encountered an error and couldn't continue. This debug screen will help you identify the problem.
                </p>
              </div>
            </div>

            <div className="bg-red-100 dark:bg-red-900/30 rounded-lg p-4 mb-4">
              <h2 className="font-semibold text-red-900 dark:text-red-300 mb-2">
                Error Message:
              </h2>
              <pre className="text-sm text-red-800 dark:text-red-200 whitespace-pre-wrap break-words font-mono">
                {error?.message || 'Unknown error'}
              </pre>
            </div>

            {error?.stack && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Stack Trace (tap to expand)
                </summary>
                <div className="bg-gray-100 dark:bg-neutral-dark-gray rounded-lg p-4 max-h-48 overflow-auto">
                  <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-mono">
                    {error.stack}
                  </pre>
                </div>
              </details>
            )}

            {errorInfo?.componentStack && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Component Stack (tap to expand)
                </summary>
                <div className="bg-gray-100 dark:bg-neutral-dark-gray rounded-lg p-4 max-h-48 overflow-auto">
                  <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words font-mono">
                    {errorInfo.componentStack}
                  </pre>
                </div>
              </details>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleReload}
                className="flex-1 bg-brand-teal hover:bg-brand-teal-hover text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Reload App
              </button>
              <button
                onClick={this.handleClearCache}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Clear Cache & Reload
              </button>
            </div>

            <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-xs text-blue-800 dark:text-blue-300">
                <strong>Tip:</strong> Take a screenshot of this error to help with debugging.
                The error has been saved to your device's storage.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
